
import React, { useEffect, useState } from "react";

import {
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";

import { useSelector } from "react-redux";

import {
  ShoppingBag,
  Leaf,
  ArrowRight,
} from "lucide-react";

import { useCart } from "../../components/CartContext";

import "./css/Header.css";

function Header() {
  const navigate = useNavigate();
  const location = useLocation();

  const { restaurantId, tableId } = useParams();

  const [menuOpen, setMenuOpen] = useState(false);

  const { cartCount } = useCart();

  // Same Redux source as Theme 9
  const restaurantName =
    useSelector((state) => state.menu?.restaurantName) ||
    "Biryani House";

  const homePath =
    `/${restaurantId}/table/${tableId}`;

  const isHomePage =
    location.pathname.replace(/\/+$/, "") === homePath;

  const closeMenu = () => {
    setMenuOpen(false);
  };

  // HOME
  const goToHome = () => {
    closeMenu();

    if (isHomePage) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      return;
    }

    navigate(homePath);
  };

  // CART
  const goToCart = () => {
    closeMenu();
    navigate(`${homePath}/cart`);
  };

  // SCROLL TO A SECTION
  const scrollToTarget = (sectionId) => {
    const element = document.getElementById(sectionId);

    if (!element) return false;

    const headerHeight =
      document.querySelector(".theme10-header")
        ?.offsetHeight || 0;

    const top =
      element.getBoundingClientRect().top +
      window.scrollY -
      headerHeight -
      12;

    window.scrollTo({
      top,
      behavior: "smooth",
    });

    return true;
  };

  const scrollToSection = (sectionId) => {
    closeMenu();

    if (isHomePage) {
      scrollToTarget(sectionId);
      return;
    }

    // Return from Cart / Checkout / Order /
    // Payment / Success to the homepage section.
    navigate(`${homePath}#${sectionId}`);
  };

  // HANDLE HASH AFTER RETURNING HOME
  useEffect(() => {
    if (!isHomePage || !location.hash) return;

    const sectionId = decodeURIComponent(
      location.hash.slice(1)
    );

    let attempts = 0;

    const timer = window.setInterval(() => {
      attempts += 1;

      const found = scrollToTarget(sectionId);

      if (found || attempts >= 30) {
        window.clearInterval(timer);
      }
    }, 100);

    return () => window.clearInterval(timer);
  }, [location.pathname, location.hash, homePath]);

  // CLOSE MOBILE MENU ON ROUTE CHANGE
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // PREVENT BACKGROUND SCROLL
  useEffect(() => {
    if (!menuOpen) return;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  return (
    <header className="theme10-header">
      <div className="theme10-header-inner">

        {/* RESTAURANT LOGO */}
        <button
          type="button"
          className="theme10-logo"
          onClick={goToHome}
          aria-label="Go to home"
        >
          <span className="theme10-logo-symbol">
            <Leaf size={19} />
          </span>

          <span className="theme10-logo-content">
            <strong>{restaurantName}</strong>
            <small>FRESHLY MADE WITH LOVE</small>
          </span>
        </button>

        {/* NAVIGATION */}
        <nav
          id="theme10-navigation"
          className={`theme10-navigation ${
            menuOpen ? "theme10-navigation-open" : ""
          }`}
          aria-label="Main navigation"
        >
          <button type="button" onClick={goToHome}>
            Home
          </button>

          <button
            type="button"
            onClick={() => scrollToSection("theme10-menu")}
          >
            Menu
          </button>

          <button
            type="button"
            onClick={() => scrollToSection("theme10-about")}
          >
            About
          </button>

          <button
            type="button"
            onClick={() => scrollToSection("theme10-popular")}
          >
            Popular
          </button>

          <button
            type="button"
            onClick={() => scrollToSection("theme10-contact")}
          >
            Contact
          </button>
        </nav>

        {/* RIGHT ACTIONS */}
        <div className="theme10-header-actions">

          <button
            type="button"
            className="theme10-header-cart"
            onClick={goToCart}
            aria-label={`Open cart. ${cartCount || 0} items`}
          >
            <ShoppingBag
              className="theme10-cart-icon"
              size={18}
            />

            <span className="theme10-cart-text">
              Cart
            </span>

            <span className="theme10-header-cart-count">
              {cartCount || 0}
            </span>
          </button>

          <button
            type="button"
            className="theme10-header-order-button"
            onClick={() => scrollToSection("theme10-menu")}
          >
            Order Now
            <ArrowRight size={15} />
          </button>

          <button
            type="button"
            className={`theme10-mobile-menu-button ${
              menuOpen ? "theme10-mobile-menu-active" : ""
            }`}
            onClick={() => setMenuOpen((previous) => !previous)}
            aria-label={
              menuOpen ? "Close navigation" : "Open navigation"
            }
            aria-expanded={menuOpen}
            aria-controls="theme10-navigation"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

        </div>
      </div>

      {/* MOBILE OVERLAY */}
      {menuOpen && (
        <button
          type="button"
          className="theme10-mobile-overlay"
          onClick={closeMenu}
          aria-label="Close navigation"
        />
      )}
    </header>
  );
}

export default Header;
